import type { FC } from 'react';
import { aboutInfo, faqList } from '@/mocks/about';
import Breadcrumb from '@/components/base/Breadcrumb';

const AboutPage: FC = () => {
  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于ASMR基佬中心',
    description: aboutInfo.brandIntro,
    url: 'https://jybmfiu.cn/about',
    mainEntity: {
      '@type': 'Organization',
      name: 'ASMR基佬中心',
      description: aboutInfo.brandIntro,
      url: 'https://jybmfiu.cn',
      email: aboutInfo.contact.email,
      address: {
        '@type': 'PostalAddress',
        addressLocality: '深圳市',
        addressRegion: '广东省',
        addressCountry: 'CN',
        streetAddress: aboutInfo.contact.address,
      },
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqList.map((item) => ({
      '@type': 'Question' as const,
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer' as const,
        text: item.answer,
      },
    })),
  };

  return (
    <div className="min-h-screen bg-background-50 pt-16">
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        <section className="py-10 md:py-16">
          <div className="text-center mb-16">
            <h1 className="text-3xl md:text-4xl font-heading font-semibold text-foreground-900 mb-4">
              关于我们
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-3xl mx-auto leading-relaxed">
              {aboutInfo.brandIntro}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-5">我们的故事</h2>
              <div className="space-y-4">
                {aboutInfo.brandStory.map((para, i) => (
                  <p key={i} className="text-sm md:text-base text-foreground-700 leading-loose">{para}</p>
                ))}
              </div>
            </div>
            <div>
              <div className="rounded-lg overflow-hidden mb-8">
                <img
                  src="https://readdy.ai/api/search-image?query=Cozy%20creative%20workspace%20with%20audio%20equipment%2C%20headphones%20on%20desk%2C%20warm%20lighting%2C%20cream%20walls%2C%20minimalist%20interior%2C%20plant%20on%20shelf%2C%20team%20working%20atmosphere%2C%20editorial%20photography%2C%20amber%20and%20beige%20tones&width=600&height=400&seq=about-story-01&orientation=landscape"
                  alt="ASMR基佬中心团队"
                  className="w-full object-cover"
                />
              </div>
              <div className="rounded-lg bg-background-100 border border-background-200/60 p-6">
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">我们的使命</h3>
                <p className="text-sm text-foreground-700 leading-relaxed">{aboutInfo.mission}</p>
              </div>
            </div>
          </div>

          <div className="mb-20">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-8 text-center">核心价值观</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {aboutInfo.values.map((v, i) => (
                <div key={i} className="p-5 rounded-lg bg-background-100 border border-background-200/60 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className={`${v.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">{v.title}</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-20">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-8 text-center">发展历程</h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200 hidden md:block"></div>
              <div className="space-y-6">
                {aboutInfo.milestones.map((m, i) => (
                  <div key={i} className={`flex flex-col md:flex-row gap-4 items-start ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="md:w-1/2"></div>
                    <div className="relative z-10 w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 flex-shrink-0">
                      <span className="text-xs font-semibold">{i + 1}</span>
                    </div>
                    <div className="md:w-1/2 bg-background-100 border border-background-200/60 rounded-lg p-5">
                      <span className="text-xs text-primary-600 font-medium">{m.year}</span>
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 mt-1 mb-1">{m.title}</h3>
                      <p className="text-xs text-foreground-600 leading-relaxed">{m.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-20">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-8 text-center">用户口碑</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {aboutInfo.testimonials.map((t, i) => (
                <div key={i} className="p-5 rounded-lg bg-background-100 border border-background-200/60">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                      <span className="text-accent-700 text-sm font-medium">{t.name[0]}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed italic">&quot;{t.content}&quot;</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-8 text-center">联系我们</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-3xl mx-auto">
              <div className="p-5 rounded-lg bg-background-100 border border-background-200/60 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                <p className="text-xs text-foreground-600">{aboutInfo.contact.email}</p>
              </div>
              <div className="p-5 rounded-lg bg-background-100 border border-background-200/60 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-wechat-line text-lg"></i>
                </div>
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">微信</h3>
                <p className="text-xs text-foreground-600">{aboutInfo.contact.wechat}</p>
              </div>
              <div className="p-5 rounded-lg bg-background-100 border border-background-200/60 text-center sm:col-span-2 lg:col-span-1">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-time-line text-lg"></i>
                </div>
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">工作时间</h3>
                <p className="text-xs text-foreground-600">{aboutInfo.contact.workingHours}</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AboutPage;